import { useRef, useState } from "react";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle } from "@/components/Kpi";
import { InstallaApp } from "@/components/InstallaApp";
import { NotifichePush } from "@/components/NotifichePush";
import { PaginaIniziale } from "@/components/PaginaIniziale";

const inp = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white text-slate-800 " +
  "focus:outline-none focus:ring-2 focus:ring-sky-300 transition-colors duration-200";
const lbl = "block text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1";

export default function Account() {
  const { user, setUser, logout, isGuest } = useAuth();
  const [nome, setNome] = useState(user?.name || "");
  const [pw, setPw] = useState({ attuale: "", nuova: "", conferma: "" });

  const salvaNome = async () => {
    try {
      const { data } = await api.put("/auth/profilo", { name: nome });
      setUser(data);
      toast.success("Nome aggiornato");
    } catch (e) { toast.error(apiError(e)); }
  };

  const fileRef = useRef(null);

  const caricaFoto = (file) => {
    if (!file) return;
    const lettore = new FileReader();
    lettore.onload = () => {
      const img = new Image();
      img.onload = async () => {
        const lato = 256;
        const cnv = document.createElement("canvas");
        cnv.width = lato;
        cnv.height = lato;
        const min = Math.min(img.width, img.height);
        cnv.getContext("2d").drawImage(img, (img.width - min) / 2, (img.height - min) / 2,
          min, min, 0, 0, lato, lato);
        try {
          const { data } = await api.put("/auth/avatar", { avatar: cnv.toDataURL("image/jpeg", 0.82) });
          setUser(data);
          toast.success("Foto profilo aggiornata");
        } catch (e) { toast.error(apiError(e)); }
      };
      img.src = lettore.result;
    };
    lettore.readAsDataURL(file);
  };

  const rimuoviFoto = async () => {
    try {
      const { data } = await api.put("/auth/avatar", { avatar: null });
      setUser(data);
      toast.success("Foto rimossa");
    } catch (e) { toast.error(apiError(e)); }
  };

  const iniziali = ((user?.name || user?.email || "?").match(/\p{L}+/gu) || ["?"])
    .slice(0, 2).map((p) => p[0]).join("").toUpperCase();

  const cambiaPassword = async () => {
    if (pw.nuova !== pw.conferma) return toast.error("Le due nuove password non coincidono");
    try {
      await api.put("/auth/password", { password_attuale: pw.attuale, password_nuova: pw.nuova });
      setPw({ attuale: "", nuova: "", conferma: "" });
      toast.success("Password aggiornata");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="account-page">
      <SectionTitle sub="Dati del tuo accesso e modifica della password.">Account</SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6" data-testid="account-dati">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">Nome utente</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {user?.avatar ? (
                <img src={user.avatar} alt="Foto profilo" data-testid="account-avatar"
                  className="h-16 w-16 rounded-full object-cover border border-slate-200" />
              ) : (
                <div data-testid="account-avatar-iniziali"
                  className="h-16 w-16 rounded-full bg-[#0018A8] text-white grid place-items-center
                    text-lg font-semibold">{iniziali}</div>
              )}
              <div className="flex flex-wrap gap-2">
                <input type="file" accept="image/*" ref={fileRef} className="hidden"
                  data-testid="account-avatar-input"
                  onChange={(e) => caricaFoto(e.target.files?.[0])} />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={isGuest}
                  data-testid="account-avatar-carica"
                  className="text-sm px-4 py-2 rounded-md border border-slate-200 text-slate-600
                    hover:border-slate-400 disabled:opacity-40 transition-colors duration-200">
                  Carica foto
                </button>
                {user?.avatar && (
                  <button type="button" onClick={rimuoviFoto} data-testid="account-avatar-rimuovi"
                    className="text-sm px-4 py-2 rounded-md border border-slate-200 text-slate-600
                      hover:border-red-400 hover:text-red-600 transition-colors duration-200">
                    Rimuovi
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className={lbl}>Email di accesso</label>
              <div className="font-mono text-sm text-slate-800" data-testid="account-email">{user?.email}</div>
            </div>
            <div>
              <label className={lbl}>Ruolo</label>
              <div className="text-sm text-slate-800" data-testid="account-ruolo">{user?.role}</div>
            </div>
            <div>
              <label className={lbl}>Nome visualizzato</label>
              <input className={inp} value={nome} data-testid="account-nome" disabled={isGuest}
                onChange={(e) => setNome(e.target.value)} />
            </div>
            <button type="button" onClick={salvaNome} disabled={isGuest || !nome.trim()} data-testid="account-salva-nome"
              className="text-sm px-5 py-2 rounded-md bg-slate-900 text-white font-semibold
                hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">
              Salva nome
            </button>
          </div>
        </Card>

        <Card className="p-6" data-testid="account-password">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">Password</h3>
          {isGuest ? (
            <p className="text-sm text-slate-500">L'accesso visitatore non ha una password.</p>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                La password è salvata cifrata e non può essere mostrata: qui puoi sostituirla.
              </p>
              <div>
                <label className={lbl}>Password attuale</label>
                <input className={inp} type="password" value={pw.attuale} data-testid="account-pw-attuale"
                  onChange={(e) => setPw({ ...pw, attuale: e.target.value })} />
              </div>
              <div>
                <label className={lbl}>Nuova password (min. 8 caratteri)</label>
                <input className={inp} type="password" value={pw.nuova} data-testid="account-pw-nuova"
                  onChange={(e) => setPw({ ...pw, nuova: e.target.value })} />
              </div>
              <div>
                <label className={lbl}>Conferma nuova password</label>
                <input className={inp} type="password" value={pw.conferma} data-testid="account-pw-conferma"
                  onChange={(e) => setPw({ ...pw, conferma: e.target.value })} />
              </div>
              <button type="button" onClick={cambiaPassword} data-testid="account-cambia-password"
                disabled={!pw.attuale || pw.nuova.length < 8 || !pw.conferma}
                className="text-sm px-5 py-2 rounded-md bg-sky-600 text-white font-semibold
                  hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">
                Aggiorna password
              </button>
            </div>
          )}
          <div className="mt-8 pt-5 border-t border-slate-100">
            <button type="button" onClick={logout} data-testid="account-logout"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-slate-300
                bg-white text-slate-600 hover:border-red-400 hover:text-red-600 transition-colors duration-200">
              <LogOut size={15} /> Esci dall'account
            </button>
          </div>
        </Card>
        <PaginaIniziale />
        <NotifichePush />
        <InstallaApp />
      </div>
    </div>
  );
}
